import { endOfDay, parseISO, startOfDay } from 'date-fns';

import type {
  ApiBookingRecurrenceRuleDto,
  BookingRefundHistoryPage,
  RefundOperationSummary,
  RefundCurrencyTotal,
  BookingPaymentStatus,
  BookingFailureReason,
  BookingWaitlistStatus,
  BookingSeatKind,
  BookingStatus,
  PaymentProvider,
} from '@mfw/common';
import type { BaseApiResponse } from './types/api.types';

import { destroy, post, get, postWithPagination } from './axios';
import { dayRangeQuery } from './dayRangeQuery';

/**
 * Seat numbers as projected FOR THE CALLER. A regular client is shown only the
 * pool they can reach, so `capacity` here is not the event's full capacity and
 * the vip fields are absent unless they hold the shared link.
 */
export interface ApiClientBookingSeats {
  availableVip?: number;
  vipCapacity?: number;
  bookedVip?: number;
  available: number;
  capacity: number;
  booked: number;
}

export interface ApiAvailableBookingEvent {
  myWaitlistEntry: null | {
    status: BookingWaitlistStatus;
    offerExpiresAt: null | string;
    id: string;
  };
  myBooking: null | {
    seatKind: BookingSeatKind;
    status: BookingStatus;
    id: string;
    /**
     * T6 (D9): the client's READ-ONLY view of their own seat's money. There
     * is no attestation control anywhere on the client side — a client
     * cancelling an onsite booking must never certify that staff handed cash
     * back (spec §3.7.1).
     */
    paymentStatus: BookingPaymentStatus;
    /** fin-stat-3 F4: the backend's ONE rule (`isRefundDue`) — money still owed on this seat. */
    refundDue: boolean;
    refundsByCurrency: RefundCurrencyTotal[];
    refundOperations: RefundOperationSummary[];
  };
  trainers?: { name: null | string; id: string }[];
  seats: ApiClientBookingSeats;
  canJoinWaitlist: boolean;
  locationText: null | string;
  onlineUrl: null | string;
  description: null | string;
  trainingLevel: null | string;
  /** shared by every event materialized from the same recurring class */
  seriesId: null | string;
  /** the tag colour the trainer picked for the slot, if any */
  color: null | string;
  cancellationDeadlineHours: number;
  membershipIds: string[];
  onlinePaymentAvailable: boolean;
  onlinePaymentProvider: PaymentProvider | null;
  /**
   * Owner decision 2026-10-29 — card payment for seats is switched off: a
   * seat no package covers is paid at the desk, and the card is not offered.
   */
  onsitePaymentOnly: boolean;
  price: null | number;
  currency: string;
  isFull: boolean;
  startsAt: string;
  endsAt: string;
  /** IANA zone the class is scheduled in — its civil day is decided here. */
  timezone: string;
  title: string;
  id: string;
  /** null = ad-hoc event, not generated from a template — see `BookingEventCoverCard`. */
  templateId: null | string;
  /**
   * The generating template's CURRENT cover, resolved server-side — the event
   * itself never copies the photo over (frozen `templateId` only), so this
   * follows the template rather than freezing at generation time. null for an
   * ad-hoc event or a template with no cover.
   */
  coverSignedUrl: null | string;
}

/** `BookingEventSeries.rule`, as read back for display — never re-expanded, just formatted. */
export type ApiBookingEventPattern = ApiBookingRecurrenceRuleDto & {
  timezone: string;
};

export interface ApiBookingEventSupportingMembership {
  id: string;
  name: string;
  price: number;
  currency: string;
}

/**
 * The full picture behind a single "Book this session" click: everything the
 * listing row already carries, plus the pieces only worth fetching one at a
 * time — the recurrence pattern, the organization's public contact info, and
 * every membership plan that covers this session (informational, not just
 * the ones the client already owns).
 */
export interface ApiBookingEventDetail extends ApiAvailableBookingEvent {
  pattern: null | ApiBookingEventPattern;
  organizationContact: {
    email: null | string;
    phoneNumber: null | string;
    phoneCountryCode: null | string;
  };
  supportingMemberships: ApiBookingEventSupportingMembership[];
}

/**
 * A booking event listed for sale on the marketplace (`listOnMarketplace`)
 * — public, no organization membership required. Unlike
 * `ApiAvailableBookingEvent`, this carries no `myBooking`/`myWaitlistEntry`/
 * `canJoinWaitlist`: those describe a member's own relationship to the
 * event, which a marketplace browser (by construction, not yet a member)
 * does not have. Buying one goes through `marketplaceId` and the generic
 * marketplace checkout, not the org-scoped booking endpoints in this file.
 */
export interface ApiMarketplaceBookingEvent {
  organizationName: string;
  organizationId: string;
  seats: { available: number; capacity: number };
  color: null | string;
  isFull: boolean;
  startsAt: string;
  endsAt: string;
  title: string;
  id: string;
  /** the `Marketplace` listing id — what `marketplaceCheckout` buys */
  marketplaceId: string;
  price: number;
  currency: string;
  /** See `ApiAvailableBookingEvent.coverSignedUrl`. */
  coverSignedUrl: null | string;
}

export interface ApiMyBooking {
  bookingEvent: {
    cancellationDeadlineHours: number;
    trainers?: { name: null | string; id: string }[];
    locationText: null | string;
    startsAt: string;
    endsAt: string;
    title: string;
    id: string;
  };
  paymentStatus: BookingPaymentStatus;
  seatKind: BookingSeatKind;
  status: BookingStatus;
  bookedAt: string;
  /** The package the seat is drawn from; null when paid for on its own. */
  userMembershipId: null | string;
  id: string;
}

export interface ApiSessionBalanceRow {
  /** null = unlimited package */
  sessionsRemaining: null | number;
  membershipName: null | string;
  sessionsTotal: null | number;
  userMembershipId: string;
  membershipId: string;
  sessionsUsed: number;
  /**
   * The package's own date range — a package only pays for a session that falls
   * inside it, so both ends are needed to tell whether it covers a given slot.
   */
  startDate: string;
  endDate: string;
  /**
   * The freeze window [freezeStartDate, freezeEndDate), exclusive end — null
   * when the package was never frozen. Present even while a freeze is only
   * scheduled or already elapsed; `packageEligibility.ts` applies it per class.
   */
  freezeStartDate: null | string;
  freezeEndDate: null | string;
  /**
   * Task 6 (membership-renewal plan) — once used up, this package opens its
   * next cycle from the class day when the client books themself (cardless
   * RECURRING with auto-renew; recorded as unpaid). Always false when staff
   * read a client's balance. The booking API stays the final authority.
   */
  renewsOnUse: boolean;
}

export interface ApiBookingAttempt {
  reason?: BookingFailureReason;
  canJoinWaitlist?: boolean;
  bookingEventId: string;
  booked: boolean;
  /** the created booking's own id — present only when `booked` is true */
  booking?: { id: string };
  /**
   * Task 5 (membership-renewal plan) — set only on
   * reason === SESSION_LIMIT_REACHED: the exhausted row the used-up-choices
   * dialog acts on, so the walk-in flow does not need a re-fetch.
   */
  userMembershipId?: string;
  endDate?: string;
}

export interface ApiWaitlistAttempt {
  reason?: BookingFailureReason;
  bookingEventId: string;
  joined: boolean;
}

const bookingsUrl = (organizationId: string) =>
  `/v2/organizations/${organizationId}/bookings`;

export const fetchAvailableBookingEvents = async ({
  from,
  organizationId,
  to,
  vipToken,
}: {
  organizationId: string;
  vipToken?: string;
  from?: string;
  to?: string;
}) => {
  const response = await get<BaseApiResponse<ApiAvailableBookingEvent[]>>(
    `${bookingsUrl(organizationId)}/available-events`,
    {
      query: {
        ...(from ? { from: startOfDay(parseISO(from)).toISOString() } : {}),
        ...(to ? { to: endOfDay(parseISO(to)).toISOString() } : {}),
        ...(vipToken ? { vipToken } : {}),
      },
    },
  );
  return response?.data ?? [];
};

/**
 * What a shared privileged-seat link (`/booking/vip/:token`) opens. Org-less
 * on purpose — the token is all the invitee has — and it carries no seat and
 * no credential, only where to go and whether the caller may book there.
 */
export interface ApiBookingVipLinkTarget {
  organizationName: string;
  bookingEventId: string;
  organizationId: string;
  /** false = the caller holds the link but is not an active member there */
  isMember: boolean;
  startsAt: string;
  title: string;
}

export const resolveBookingVipLink = async ({ token }: { token: string }) => {
  const response = await get<BaseApiResponse<ApiBookingVipLinkTarget>>(
    `/v2/bookings/vip/${encodeURIComponent(token)}`,
  );
  return response?.data;
};

export const fetchBookingEventDetail = async ({
  bookingEventId,
  organizationId,
}: {
  bookingEventId: string;
  organizationId: string;
}) => {
  const response = await get<BaseApiResponse<ApiBookingEventDetail>>(
    `${bookingsUrl(organizationId)}/available-events/${bookingEventId}`,
  );
  return response?.data ?? null;
};

export const fetchBookingSeriesOccurrences = async ({
  from,
  limit,
  organizationId,
  seriesId,
}: {
  organizationId: string;
  seriesId: string;
  from?: string;
  limit?: number;
}) => {
  const response = await get<BaseApiResponse<ApiAvailableBookingEvent[]>>(
    `${bookingsUrl(organizationId)}/series/${seriesId}/occurrences`,
    { query: { from, limit: limit !== undefined ? String(limit) : undefined } },
  );
  return response?.data ?? [];
};

export const fetchMarketplaceBookingEvents = async ({
  limit,
  offset,
}: {
  limit?: number;
  offset?: number;
} = {}) => {
  const response = await postWithPagination<ApiMarketplaceBookingEvent[]>(
    '/v2/marketplace/booking-events',
    { limit, offset },
  );
  return { data: response?.data ?? [], total: response?.total ?? 0 };
};

export const fetchMyBookings = async ({
  clientUserId,
  from,
  includeCancelled,
  organizationId,
  timeZone,
  to,
}: {
  /** Staff only: one client's seats instead of the caller's own. */
  clientUserId?: string;
  includeCancelled?: boolean;
  organizationId: string;
  from?: string;
  to?: string;
  /** Cuts `from`/`to` at this zone's midnight instead of the browser's. */
  timeZone?: string;
}) => {
  const response = await get<BaseApiResponse<ApiMyBooking[]>>(
    bookingsUrl(organizationId),
    {
      query: {
        ...dayRangeQuery(from, to, timeZone),
        ...(clientUserId ? { clientUserId } : {}),
        ...(includeCancelled ? { includeCancelled: 'true' } : {}),
      },
    },
  );
  return response?.data ?? [];
};

export const fetchSessionBalance = async ({
  organizationId,
}: {
  organizationId: string;
}) => {
  const response = await get<BaseApiResponse<ApiSessionBalanceRow[]>>(
    `${bookingsUrl(organizationId)}/session-balance`,
  );
  return response?.data ?? [];
};

/**
 * Books several slots in one call. Always resolves: each requested slot comes
 * back with its own outcome, so a slot that filled up in the meantime never
 * discards the ones that succeeded.
 */
export const createBookings = async ({
  bookingEventIds,
  clientUserId,
  ignoreMemberships,
  isTrial,
  organizationId,
  paymentMethod,
  prospectId,
  userMembershipId,
  vipToken,
}: {
  paymentMethod?: 'ONLINE' | 'ONSITE';
  bookingEventIds: string[];
  /** which package to draw the session from; omitted = let the API choose */
  userMembershipId?: string;
  organizationId: string;
  vipToken?: string;
  /**
   * Staff-only: book a walk-in on this client's behalf instead of the
   * caller's own account. The API forces ONSITE payment whenever this is set,
   * so `paymentMethod` never needs to be sent alongside it.
   */
  clientUserId?: string;
  /**
   * Task 5 (membership-renewal plan) — the used-up-choices dialog's "book
   * without membership (pay on site)" option. Staff-only, requires
   * `clientUserId`; skips membership resolution and books ONSITE with
   * `userMembershipId: null`.
   */
  ignoreMemberships?: boolean;
  /**
   * Staff-only: seat a walk-in with NO account (a Prospect) on a regular seat.
   * Exclusive with `clientUserId`; always ONSITE and never uses a package.
   */
  prospectId?: string;
  /** "Probni trening" — only with `prospectId`; a free visit, nothing owed. */
  isTrial?: boolean;
}) => {
  const response = await post<ApiBookingAttempt[]>(
    bookingsUrl(organizationId),
    {
      bookingEventIds,
      clientUserId,
      ignoreMemberships,
      isTrial,
      paymentMethod,
      prospectId,
      userMembershipId,
      vipToken,
    },
  );
  return response?.data ?? [];
};

export const cancelBooking = async ({
  bookingId,
  organizationId,
}: {
  organizationId: string;
  bookingId: string;
}) => {
  const response = await post<{ id: string }>(
    `${bookingsUrl(organizationId)}/${bookingId}/cancel`,
  );
  // `meta` says whether the session/money came back — the caller shows it, so
  // the client is told the truth instead of a generic "cancelled".
  return response as unknown as {
    meta?: {
      waitlistOffersSent: number;
      paymentRefundDue: boolean;
      deskRefundDue: boolean;
      sessionPayment: boolean;
      sessionReturned: boolean;
      withinDeadline: boolean;
    };
  };
};

export const joinBookingWaitlist = async ({
  bookingEventIds,
  organizationId,
  vipToken,
}: {
  bookingEventIds: string[];
  organizationId: string;
  vipToken?: string;
}) => {
  const response = await post<ApiWaitlistAttempt[]>(
    `${bookingsUrl(organizationId)}/waitlist`,
    { bookingEventIds, vipToken },
  );
  return response?.data ?? [];
};

export const acceptWaitlistOffer = async ({
  entryId,
  organizationId,
}: {
  organizationId: string;
  entryId: string;
}) => {
  const response = await post<{ id: string }>(
    `${bookingsUrl(organizationId)}/waitlist/${entryId}/accept`,
  );
  // B-13 — the caller's other queued slots that clash with the one just taken.
  // Reported, never acted on: which of two overlapping classes they want is
  // theirs to decide.
  return response as unknown as {
    warnings?: { title: string; startsAt: string; bookingEventId: string }[];
  };
};

export const declineWaitlistOffer = async ({
  entryId,
  organizationId,
}: {
  organizationId: string;
  entryId: string;
}) =>
  post<{ id: string }>(
    `${bookingsUrl(organizationId)}/waitlist/${entryId}/decline`,
  );

export const leaveBookingWaitlist = async ({
  entryId,
  organizationId,
}: {
  organizationId: string;
  entryId: string;
}) => destroy(`${bookingsUrl(organizationId)}/waitlist/${entryId}`);

export const fetchBookingRefundHistory = async (args: {
  organizationId?: string;
  perspective: 'own' | 'staff';
  cursor?: string;
}) => {
  const response = await get<BookingRefundHistoryPage>(
    args.perspective === 'staff'
      ? `${bookingsUrl(args.organizationId ?? '')}/refund-history`
      : '/v2/auth/me/booking-refunds',
    {
      query: {
        limit: '20',
        ...(args.cursor ? { cursor: args.cursor } : {}),
        ...(args.perspective === 'own' && args.organizationId
          ? { organizationId: args.organizationId }
          : {}),
      },
    },
  );
  if (!response) throw new Error('Refund history unavailable');
  return response;
};
